import { Code, ConnectError } from "@connectrpc/connect";
import { atom } from "jotai";

import type { EnvironmentClassEntry } from "@/hooks/use-environment-class-entries";
import type { PlainAuthenticationMethodPersonalAccessToken, PlainRunner } from "@/queries/runner-queries";

export const activeTabAtom = atom<TabValue | undefined>(undefined);
export const escapePressCallbackAtom = atom<null | { fn: () => void }>(null);
export const selectedClassEntryAtom = atom<EnvironmentClassEntry | undefined>(undefined);

export const AUTOSTART_PREFERENCE_KEY = "AUTOSTART_PREFERENCES";

// Backend enforces 10 KiB max for preference values (SetPreferenceRequest.value max_len: 10240)
const MAX_PREFERENCE_VALUE_BYTES = 10240;

/**
 * Trims the oldest entries from an array until its JSON-serialized form fits
 * within the backend's preference value size limit. Entries are removed from
 * the front (oldest first, assuming append-to-end ordering).
 */
export function capPreferenceArray<T>(entries: T[], maxBytes: number = MAX_PREFERENCE_VALUE_BYTES): T[] {
    let result = entries;
    while (result.length > 0 && JSON.stringify(result).length > maxBytes) {
        result = result.slice(1);
    }
    return result;
}

export type CreateEnvironmentStage =
    | { type: "input" }
    | {
          type: "scm-authentication";
          authenticationUrl: string;
          patAuth?: PlainAuthenticationMethodPersonalAccessToken;
          scmId: string;
          scmName: string;
          runner: PlainRunner;
          contextUrl: string;
          onClickContinue?: () => void;
      };

export const TabsValues = {
    project: "project",
    url: "url",
    scratch: "scratch",
    home: "home", // home is only supported in the v2 of CreateEnvironmentDetailsLayout
} as const;
export type TabValue = (typeof TabsValues)[keyof typeof TabsValues];

export const isNoSuitableRunnerFoundError = (error: unknown) => {
    return (
        error instanceof ConnectError &&
        error.code === Code.NotFound &&
        error.message.includes("no suitable runner found")
    );
};
